import type { ReservationAction } from '@/features/reservations/model/reservation-actions';
import { useT } from '@/i18n';
import { getStatusColor } from '@/utils/status';
import type { Reservation } from '@restaurant/api-client';
import { ReservationStatus } from '@restaurant/types';
import { List, ListItem, Popover } from 'framework7-react';
import { forwardRef, type MutableRefObject } from 'react';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type PopoverInstance = { el: HTMLElement | null; f7Popover: () => any };

interface ReservationActionsProps {
  reservation: Reservation | null;
  allowedActions: ReadonlySet<ReservationAction>;
  onClose: () => void;
  onConfirm: (reservation: Reservation) => void;
  onReactivate: (reservation: Reservation) => void;
  onReject: (reservation: Reservation) => void;
  onCancel: (reservation: Reservation) => void;
  onMarkArrived: (reservation: Reservation) => void;
  onMarkSeated: (reservation: Reservation) => void;
  onMarkInProgress: (reservation: Reservation) => void;
  onMarkCompleted: (reservation: Reservation) => void;
  onMarkNoShow: (reservation: Reservation) => void;
  onMarkNotStarted: (reservation: Reservation) => void;
  onReschedule: (reservation: Reservation) => void;
}

const ReservationActions = forwardRef<PopoverInstance, ReservationActionsProps>(
  function ReservationActions(
    {
      reservation,
      allowedActions,
      onClose,
      onConfirm,
      onReactivate,
      onReject,
      onCancel,
      onMarkArrived,
      onMarkSeated,
      onMarkInProgress,
      onMarkCompleted,
      onMarkNoShow,
      onMarkNotStarted,
      onReschedule,
    },
    ref,
  ) {
    const t = useT();
    const renderContent = () => {
      if (!reservation) return null;
      const r = reservation;

      const item = (label: string, color: string, icon: string, onClick: () => void) => (
        <ListItem
          key={label}
          title={label}
          link="#"
          style={{ color }}
          onClick={(e: React.MouseEvent) => {
            e.preventDefault();
            onClose();
            onClick();
          }}
        >
          <i slot="media" className="f7-icons" style={{ color, fontSize: 22 }}>
            {icon}
          </i>
        </ListItem>
      );

      // The current state, shown as a non-actionable greyed anchor (with a
      // trailing checkmark) so the status list makes the "you are here" obvious.
      const MUTED = '#8e8e93';
      const currentItem = (label: string) => (
        <ListItem key={`current-${label}`} title={label} style={{ color: MUTED }}>
          <i slot="media" className="f7-icons" style={{ color: MUTED, fontSize: 22 }}>
            checkmark_seal_fill
          </i>
          <i slot="after" className="f7-icons" style={{ color: MUTED, fontSize: 18 }}>
            checkmark
          </i>
        </ListItem>
      );

      const list = (items: React.ReactNode) => (
        <List noChevron style={{ minWidth: 220, margin: 0 }}>
          {items}
        </List>
      );

      // Action rows are coloured by *intent*, not by destination state - so
      // destructive actions stay red even when their terminal state is gray,
      // positive actions stay green, warnings stay orange. The intent colours
      // line up with the badge colours for states that already carry the same
      // semantic (NO_SHOW orange, REJECTED red, ARRIVED purple).
      const confirmColor = '#248a3d'; // vivid green - positive lifecycle move
      const rejectColor = getStatusColor('rejected'); // red - same as REJECTED badge
      const cancelColor = '#ff3b30'; // red - destructive (CANCELLED badge stays gray)
      const arrivedColor = getStatusColor('arrived'); // purple - same as ARRIVED badge
      const seatedColor = getStatusColor('seated'); // teal - same as SEATED badge
      const inProgressColor = getStatusColor('in-progress'); // violet - same as IN_PROGRESS badge
      const completedColor = '#248a3d'; // vivid green - successful completion
      const noShowColor = getStatusColor('no-show'); // orange - same as NO_SHOW badge

      const hasAction = (action: ReservationAction) => allowedActions.has(action);
      const hasArrivalAction =
        hasAction('markArrived') ||
        hasAction('markSeated') ||
        hasAction('markInProgress') ||
        hasAction('markCompleted') ||
        hasAction('markNoShow') ||
        hasAction('markNotStarted') ||
        hasAction('reschedule');
      let contextual: React.ReactNode = null;

      if (hasAction('confirm')) {
        contextual = (
          <>
            {list(
              <>
                {item(t.reservations.menu.confirm, confirmColor, 'checkmark_circle_fill', () =>
                  onConfirm(r),
                )}
              </>,
            )}
            {list(
              <>
                {item(t.reservations.menu.decline, rejectColor, 'xmark_circle_fill', () =>
                  onReject(r),
                )}
              </>,
            )}
          </>
        );
      } else if (hasAction('reactivate')) {
        contextual = list(
          <>
            {item(t.reservations.menu.reactivate, confirmColor, 'arrow_2_circlepath', () =>
              onReactivate(r),
            )}
          </>,
        );
      } else if (r.status === ReservationStatus.REJECTED) {
        contextual = list(currentItem(t.reservations.displayStatus.rejected));
      } else if (hasAction('cancel') && !hasArrivalAction) {
        contextual = list(
          <>{item(t.reservations.menu.cancel, cancelColor, 'xmark_circle', () => onCancel(r))}</>,
        );
      } else if (hasAction('markArrived')) {
        contextual = (
          <>
            {list(
              <>
                {item(
                  t.reservations.menu.markArrived,
                  arrivedColor,
                  'arrow_right_circle_fill',
                  () => onMarkArrived(r),
                )}
              </>,
            )}
            {list(
              <>
                {item(t.reservations.menu.markNoShow, noShowColor, 'minus_circle_fill', () =>
                  onMarkNoShow(r),
                )}
                {item(t.reservations.menu.cancel, cancelColor, 'xmark_circle', () => onCancel(r))}
              </>,
            )}
          </>
        );
      } else if (hasAction('markNotStarted')) {
        const notStartedColor = getStatusColor('waiting');
        const rescheduledColor = getStatusColor('rescheduled');
        contextual = (
          <>
            {list(
              <>
                {item(t.reservations.menu.markNotStarted, notStartedColor, 'circle', () =>
                  onMarkNotStarted(r),
                )}
                {item(
                  t.reservations.menu.markInProgress,
                  arrivedColor,
                  'arrow_right_circle_fill',
                  () => onMarkArrived(r),
                )}
                {currentItem(t.reservations.displayStatus.completed)}
                {item(t.reservations.menu.markNoShow, noShowColor, 'minus_circle_fill', () =>
                  onMarkNoShow(r),
                )}
                {hasAction('reschedule') &&
                  item(
                    t.reservations.menu.markRescheduled,
                    rescheduledColor,
                    'arrow_2_circlepath',
                    () => onReschedule(r),
                  )}
              </>,
            )}
            {list(
              <>
                {item(t.reservations.menu.cancel, cancelColor, 'xmark_circle', () => onCancel(r))}
              </>,
            )}
          </>
        );
      } else if (hasAction('markCompleted')) {
        contextual = (
          <>
            {list(
              <>
                {hasAction('markSeated') &&
                  item(t.reservations.menu.markSeated, seatedColor, 'person_crop_circle_fill', () =>
                    onMarkSeated(r),
                  )}
                {hasAction('markInProgress') &&
                  item(
                    t.reservations.menu.markInProgress,
                    inProgressColor,
                    'play_circle_fill',
                    () => onMarkInProgress(r),
                  )}
                {item(
                  t.reservations.menu.markCompleted,
                  completedColor,
                  'checkmark_seal_fill',
                  () => onMarkCompleted(r),
                )}
              </>,
            )}
            {list(
              <>
                {item(t.reservations.menu.cancel, cancelColor, 'xmark_circle', () => onCancel(r))}
              </>,
            )}
          </>
        );
      }

      return contextual;
    };

    return (
      <Popover
        ref={ref as MutableRefObject<PopoverInstance>}
        onPopoverClosed={onClose}
        style={{ minWidth: 220 }}
      >
        {renderContent()}
      </Popover>
    );
  },
);

export default ReservationActions;
